import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { SearchReport } from '../components/SearchReport';
import { apiService } from '../services/apiService';
import { useToast } from '../contexts/ToastContext';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { useAuth } from '../contexts/AuthContext';

interface SearchReportData {
  search_id: number;
  parameter_type: string;
  search_value: string;
  country_code?: string | null;
  status: string;
  total_cost: number;
  created_at: string;
  completed_at: string | null;
  results: Array<{
    api_id: number;
    api_name: string;
    status: 'success' | 'failed';
    data: Record<string, any>;
    raw_response: Record<string, any>;
    processing_time: number;
    error_message: string;
  }>;
  summary: {
    total_apis_called: number;
    successful_apis: number;
    failed_apis: number;
    total_processing_time: number;
  };
  ai_summary?: string | null;
  ai_summary_generated?: boolean;
  ai_summary_generated_at?: string | null;
  ai_summary_cost?: number;
}

export const SearchReportPage: React.FC = () => {
  const { searchId } = useParams<{ searchId: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { refreshUser } = useAuth();
  const [report, setReport] = useState<SearchReportData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  


  const loadReport = async (forceRefresh = false) => {
    if (!searchId) {
      setError('Search ID is required');
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      
      // Force refresh by adding timestamp if needed
      const reportData = await apiService.getSearchReport(parseInt(searchId));
      setReport(reportData);
    } catch (err) {
      console.error('Failed to load search report:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to load search report';
      setError(errorMessage);
      showToast(errorMessage, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadReport();
  }, [searchId, showToast]);

  const handleDownloadPDF = async () => {
    if (!searchId) return;

    try {
      showToast('Generating PDF report...', 'info');
      const exportData = await apiService.exportSearchResults(parseInt(searchId), 'pdf');
      const blob = await apiService.downloadExport(exportData.export_id);
      
      // Create download link
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `osint-report-${searchId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      showToast('PDF report downloaded successfully', 'success');
    } catch (err) {
      console.error('Failed to download PDF:', err);
      showToast('Failed to download PDF report', 'error');
    }
  };

  const handleClose = async () => {
    // Refresh user to update Int Coins after viewing report
    try {
      await refreshUser();
    } catch (e) {
      // Non-blocking
      console.warn('Failed to refresh user on close:', e);
    }
    navigate('/app/search');
  };



  const handleRefreshReport = async () => {
    await loadReport(true);
    try {
      await refreshUser();
    } catch (e) {
      console.warn('Failed to refresh user on report update:', e);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <Card className="p-8">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mb-4"></div>
            <p className="text-gray-600 dark:text-gray-300">Loading search report...</p>
            <p className="text-sm text-gray-400 dark:text-gray-500 mt-2">Search ID: {searchId}</p>
          </div>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <Card className="p-8">
          <div className="text-center">
            <div className="text-red-600 dark:text-red-400 mb-4">
              <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Error Loading Report</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-4">{error}</p>
            <div className="space-x-3">
              <Button onClick={() => window.location.reload()}>
                Try Again
              </Button>
              <Button variant="outline" onClick={handleClose}>
                Back to Search
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <Card className="p-8">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Report Not Found</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-4">The requested search report could not be found.</p>
            <Button variant="outline" onClick={handleClose}>
              Back to Search
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-6xl mx-auto px-4">
        <SearchReport 
          report={report}
          onClose={handleClose}
          onDownloadPDF={handleDownloadPDF}
          onRefreshReport={handleRefreshReport}
        />
      </div>
    </div>
  );
}; 